import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, AlertTriangle, XCircle, Clock, Server, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  operational: { label: 'Todos os Sistemas Operacionais', icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50 border-emerald-200', bar: 'bg-emerald-500' },
  degraded: { label: 'Alguns Serviços com Alerta', icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200', bar: 'bg-amber-500' },
  major_outage: { label: 'Incidentes em Andamento', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50 border-red-200', bar: 'bg-red-500' },
};

const STATUS_LABELS = { operational: 'Operacional', degraded: 'Atenção', major_outage: 'Crítico' };
const SEVERITY_COLORS = { 0: '#97AAB3', 1: '#3B82F6', 2: '#EAB308', 3: '#F97316', 4: '#EF4444', 5: '#DC2626' };

export function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/status-page`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 60000); return () => clearInterval(i); }, [fetchData]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-[400px] w-full" /></div>;

  const overall = STATUS_CONFIG[data?.overall_status] || STATUS_CONFIG.operational;
  const OverallIcon = overall.icon;

  return (
    <div className="space-y-6" data-testid="status-page">
      {/* Overall Status Banner */}
      <Card className={`border-2 ${overall.bg}`}>
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <OverallIcon className={`h-10 w-10 ${overall.color}`} />
            <div>
              <h2 className="text-2xl font-bold">{overall.label}</h2>
              <p className="text-sm text-muted-foreground">
                {data?.total_hosts || 0} hosts monitorados • {data?.total_problems || 0} problemas ativos
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground">Atualizado: {new Date().toLocaleTimeString('pt-BR')}</p>
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Services Grid */}
      <div className="space-y-2">
        {(data?.services || []).map((svc, i) => {
          const st = STATUS_CONFIG[svc.status] || STATUS_CONFIG.operational;
          const StIcon = st.icon;
          return (
            <Card key={i} className="border">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <StIcon className={`h-5 w-5 ${st.color} shrink-0`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{svc.name}</span>
                      <span className="text-xs text-muted-foreground">({svc.hosts_total} hosts)</span>
                    </div>
                    {/* Uptime bar */}
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${st.bar}`} style={{ width: `${svc.uptime}%` }} />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground w-14 text-right">{svc.uptime}%</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 ml-4">
                  {svc.hosts_down > 0 && <Badge className="bg-red-500 text-white text-xs">{svc.hosts_down} down</Badge>}
                  {svc.hosts_degraded > 0 && <Badge className="bg-amber-500 text-white text-xs">{svc.hosts_degraded} degraded</Badge>}
                  <Badge variant="outline" className={`text-xs ${st.color}`}>{STATUS_LABELS[svc.status] || st.label}</Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Incidents */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />Incidentes Recentes (24h)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.recent_incidents || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhum incidente nas últimas 24h</p>
          ) : (
            <div className="space-y-2">
              {data.recent_incidents.map((inc, i) => (
                <div key={inc.id || i} className="flex items-center gap-3 p-2 rounded border text-sm">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: SEVERITY_COLORS[inc.severity] }} />
                  <span className="flex-1 truncate">{inc.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0"><Server className="inline h-3 w-3 mr-1" />{inc.host}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{formatDate(inc.time)}</span>
                  {inc.resolved ? (
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Resolvido</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Ativo</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
